import React, { ReactElement } from 'react';
import ScreenOrientation from 'react-native-orientation-locker';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import { ProductReferences } from 'common/constants/enums';
import { getDefaultRoute } from 'components/Routes/getDefaultRoute';
import { useSafeParams } from 'components/Routes/useSafeParams';
import { useDispatchPathToParent } from 'developer/hooks/useDispatchPathToParent';
import { withContentPlaceholderWrapper } from 'lib/error/ErrorBoundary/utils/withContentPlaceholderWrapper/withContentPlaceholderWrapper';
import { createErrorBoundaryForDomain } from 'lib/error/utils/createErrorBoundaryForDomain';

import { DaylightSwitch } from './DaylightSwitch';
import { SleepioSwitch } from './SleepioSwitch';
import { StoryBook } from './StoryBook';
import { useBackButtonTriggerForPath } from './useBackButtonTriggerForPath';

const ErrorBoundaryForRoutes = createErrorBoundaryForDomain(
  'components/Routes'
);

import { useInitializeAPIClient } from 'lib/api/hooks/useInitializeAPIClient';
import { useQueryProgram } from 'lib/api/reactQueryHelpers';

import { ForceUpdateGate } from './ForceUpdateGate/ForceUpdateGate';
import { useInitUserInfo } from './hooks/useInitUserInfo';

export const ProductRoutesWithProgram = (): ReactElement => {
  const { productReference } = useSafeParams();
  useQueryProgram();
  useInitUserInfo();

  if (productReference === ProductReferences.DAYLIGHT) {
    ScreenOrientation.lockToPortrait();
    return <DaylightSwitch />;
  }
  return <SleepioSwitch />;
};

export const ProductRoutes = (): ReactElement => {
  useBackButtonTriggerForPath();
  const clientInitialized = useInitializeAPIClient();

  if (!clientInitialized) {
    return <></>;
  }
  return (
    <ForceUpdateGate>
      <ProductRoutesWithProgram />
    </ForceUpdateGate>
  );
};

const Routes = (): ReactElement => {
  const history = useHistory();
  useDispatchPathToParent();
  if (['', '/'].includes(history.location.pathname)) {
    // Redirect only works satisfactorily on first load and not subsequent page reloaded. Because
    // of the way this works in tests this presents a problem of the app redirecting back to
    // the root page when we don't . So we test for
    return <Redirect exact from="/" to={getDefaultRoute()} />;
  }

  return (
    <ErrorBoundaryForRoutes
      fallbackRenderWithWrapper={withContentPlaceholderWrapper}
    >
      <Switch>
        <Route path={'/:productReference'}>
          <ProductRoutes />
        </Route>
        <Route path={'/storybook'} component={StoryBook} />
        <Route path={'/storybook/:story(.*)'} component={StoryBook} />
      </Switch>
    </ErrorBoundaryForRoutes>
  );
};

export default Routes;
